<!-- https://www.figma.com/design/K7vDg6el3Bz7MHUdlMREDj/Credit-Conversion-gotes?node-id=2027-58&p=f&m=dev -->
<template>
  <div class="add-to-transcript-page">
    <div class="add-to-transcript-page__content">
      <div v-if="Result.isNotAsked(got) || Result.isLoading(got)">
        <Spinner :size="32" />
      </div>
      <div v-else-if="Result.isErr(got)">
        <CardSwitch :status="'error'" :is-polling="false" />
      </div>
      <div v-else-if="Result.isOk(got)">
        <CardSwitch :status="got.value.status" :is-polling="isPolling" />
      </div>
    </div>
  </div>
</template>

<script>
// @ts-check
import { NotAsked, Loading, Err, Ok, isErr, Result } from '@crvouga/result';
import { AddToTranscriptAPI } from './add-to-transcript-api';
import Spinner from './ui/Spinner.vue';
import CardSwitch from './CardSwitch.vue';
import { isNonEmptyString } from './lib/is-non-empty-string';

const addToTranscriptAPI = AddToTranscriptAPI({ type: 'ulc-api-hub' });

/**
 * @typedef {{
 * got: import('@crvouga/result').RemoteResult<got, Error>
 * posted: import('@crvouga/result').RemoteResult<posted, Error>
 * pollingInterval: number | null
 * pollingStartTime: number | null
 * }} Data
 */

export default {
  name: 'AddToTranscript',
  components: {
    Spinner,
    CardSwitch
  },
  /**
   * @returns {Data}
   */
  data() {
    return {
      Result,
      got: NotAsked(),
      posted: NotAsked(),
      pollingInterval: null,
      pollingStartTime: null
    };
  },
  async mounted() {
    // First call POST to initiate the credit request, then poll GET for status
    await this.post();
  },

  beforeUnmount() {
    this.stopPolling();
  },
  computed: {
    isPolling() {
      return this.pollingInterval !== null;
    }
  },
  methods: {
    async post() {
      this.posted = Loading();

      const gotInput = await this.getInputFromQueryParams();

      if (isErr(gotInput)) {
        this.posted = gotInput;
        return;
      }

      const { asurite, enrollmentId } = gotInput.value;

      const posted = await addToTranscriptAPI.post({ asurite, enrollmentId });

      this.posted = posted;

      // After POST request, check if it was successful or if there was an error
      if (Result.isOk(posted)) {
        // Check if POST returned a final status that doesn't need polling
        const finalStatuses = ['already-processed', 'approved', 'success', 'error', 'early-retry'];
        if (posted.value.status && finalStatuses.includes(posted.value.status)) {
          // Set the final status directly without calling GET
          this.got = Ok(posted.value);
        } else {
          // For pending or other statuses, call GET to check status
          await this.get();
        }
      } else if (Result.isErr(posted)) {
        // Handle POST error by setting got state to show error
        this.got = Err(posted.error);
      }
    },

    async get() {
      this.got = Loading();

      const gotInput = await this.getInputFromQueryParams();

      if (isErr(gotInput)) {
        this.got = gotInput;
        return;
      }

      const { asurite, enrollmentId } = gotInput.value;

      const got = await addToTranscriptAPI.get({ asurite, enrollmentId });

      this.got = got;

      // Start polling if status is pending
      if (Result.isOk(got) && got.value.status === 'pending') {
        this.startPolling();
      }
    },

    /**
     * @returns {Promise<import('@crvouga/result').Result<{ asurite: string; enrollmentId: string }, Error>>}
     */
    async getInputFromQueryParams() {
      const asurite = this.$route.query.asurite;

      if (!isNonEmptyString(asurite)) {
        return Err(new Error('`asurite` is missing in URL query params'));
      }

      const enrollmentId = this.$route.query.enrollmentId;

      if (!isNonEmptyString(enrollmentId)) {
        return Err(new Error('`enrollmentId` is missing in URL query params'));
      }

      return Ok({ asurite, enrollmentId });
    },

    startPolling() {
      if (this.pollingInterval) {
        return; // Already polling
      }

      this.pollingStartTime = Date.now();

      this.pollingInterval = setInterval(async () => {
        // Stop polling after 5 minutes
        const maxPollingTime = 5 * 60 * 1000; // 5 minutes
        const currentTime = Date.now();

        if (currentTime - this.pollingStartTime > maxPollingTime) {
          this.stopPolling();
          return;
        }

        // Poll for status updates
        await this.pollStatus();
      }, 5000); // Poll every 5 seconds
    },

    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
        this.pollingStartTime = null;
      }
    },

    async pollStatus() {
      try {
        const gotInput = await this.getInputFromQueryParams();

        if (Result.isErr(gotInput)) {
          return;
        }

        const { asurite, enrollmentId } = gotInput.value;
        const got = await addToTranscriptAPI.get({ asurite, enrollmentId });

        this.got = got;

        // Stop polling if status is no longer pending or if there's an error
        if (Result.isOk(got) && got.value.status !== 'pending') {
          this.stopPolling();
        } else if (Result.isErr(got)) {
          this.stopPolling();
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error polling status:', error);
        // Stop polling on network or other errors
        this.stopPolling();
      }
    }
  }
};
</script>

<style>
.add-to-transcript-page {
  display: flex;
  flex-direction: column;
}

.add-to-transcript-page__content {
  display: flex;
  flex-direction: column;
  min-height: 80vh;
  justify-content: center;
  align-items: center;
}
</style>
